import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
	Background,
	Controls,
	ReactFlowProvider,
	useNodesInitialized,
	useNodesState,
	useReactFlow,
	type Node,
} from "reactflow";
import "reactflow/dist/style.css";
import { DataStateView } from "../../../kontrolEdge/components/Backbone/BackboneDetails/StateViews/DataStateView";
import { insightNodeTypeFor, insightReactFlowNodeTypes, type InsightNodeData } from "./topologyNodes";
import { normalizeCanonicalTopologyNodes } from "./canonicalDigest";
import { normalizeCardDimensions } from "./cardLayout";
import { applyEniDisclosure } from "./eniDisclosure";
import {
	applyHierarchyDisclosure,
	computeForceExpansionForMatches,
	DEFAULT_HIERARCHY_FOCUS,
	DEFAULT_VISIBLE_AZ_LIMIT,
	DEFAULT_VISIBLE_VPC_LIMIT,
	OVERVIEW_GRID_COLUMNS,
	type HierarchyFocusState,
} from "./hierarchyDisclosure";
import {
	computeAdaptiveOverviewCapacity,
	computeReadableZoomFloor,
	computeScopedViewport,
	OVERVIEW_FIT_PADDING,
	type ViewportPixelSize,
} from "./adaptiveViewport";
import { TopologyBreadcrumbs, type TopologyBreadcrumbSegment } from "./TopologyBreadcrumbs";
import {
	isSupportedInsightTopologyProfile,
	type InsightChipRef,
	type InsightConnectivityRef,
	type InsightRequestState,
	type InsightTopologyResponse,
} from "../../types/insightApi";

import { describeEmptyState } from "./insightEmptyState";

export interface InsightTopologyCanvasProps {
	topology: InsightTopologyResponse | null;
	status: InsightRequestState;
	errorMessage: string | null;
	onRetry: () => void;
	selectedResourceId: string | null;
	onSelectResource: (id: string | null) => void;
	/**
	 * Overrides the description derived from the payload's `state`
	 * (CTX-422). Left undefined, the canvas explains the empty result
	 * itself — see `describeEmptyState`.
	 */
	emptyDescription?: string;
	/**
	 * Resource-search query (CTX-112: lifted out of this component so the
	 * compact page-header toolbar can own the input while this canvas stays
	 * the single source of highlighting/filtering truth). Defaults to "" so
	 * existing callers that don't yet control search keep working.
	 */
	search?: string;
}

const GRID_COLUMNS = 6;
const GRID_CELL = 180;

/**
 * CTX-173: before the canvas's own pixel size has been measured (the very
 * first render, or a test that never mocks `clientWidth`/`clientHeight`),
 * `adaptiveViewport.ts` can't compute anything real — this is the only
 * fallback used in that narrow window, and it matches this codebase's
 * previous (rejected) fixed floor only coincidentally, not by design. Once
 * `measuredViewportSize` is available every subsequent render uses the real,
 * geometry-derived floor instead — see `readableZoomFloor` below.
 */
const PRE_MEASUREMENT_FALLBACK_MIN_ZOOM = 0.35;

/** Stable identity so `<ReactFlow edges={...}>` never gets a fresh array reference on every render (CTX-113: the overview never renders edges — route-table/gateway associations render as family icon chips on the cards instead). */
const NO_EDGES: never[] = [];

/**
 * Camera moves animate only when the document is actually visible: d3-zoom
 * transitions advance on requestAnimationFrame, which Chrome suspends for
 * hidden/occluded tabs — a 200ms pan started there simply never lands, and
 * the user returns to a camera pointing at the pre-focus coordinates.
 * Verified live 2026-08-04 (transform frozen across 4s in a hidden tab while
 * a synchronous wheel zoom applied instantly).
 */
function cameraMoveDuration(): number {
	return typeof document !== "undefined" && document.visibilityState === "visible" ? 200 : 0;
}

/**
 * CTX-128 "ENI-centric subnet rendering": an EC2/Compute-instance resource
 * node is never rendered as its own subnet-child card — only its ENIs are,
 * each decorated with a `resolvedInstance` reference back to this same
 * instance id, so an instance with multiple ENIs across different subnets
 * never produces duplicate instance cards. The instance node itself is NOT
 * deleted from the digest — `ResourceDetailPanel` still resolves it by id
 * straight off `topology.nodes` when a `resolvedInstance` chip is clicked —
 * it is only excluded from what gets handed to ReactFlow here. Any node
 * that referenced a suppressed instance as its own `parentId` (e.g. an older
 * digest shape nesting an ENI under its owning instance rather than the
 * subnet directly) is reparented one level up so nothing becomes silently
 * orphaned by this presentation-only suppression.
 */
function suppressInstanceContainers(nodes: InsightTopologyResponse["nodes"]): InsightTopologyResponse["nodes"] {
	const suppressedParentOf = new Map<string, string | undefined>();
	nodes.forEach((node) => {
		const data = (node.data ?? {}) as InsightNodeData;
		const groupKind = node.groupKind ?? data.groupKind;
		const kind = node.kind === "group" ? groupKind : node.type ?? data.kind;
		if (insightNodeTypeFor(kind, node.kind) === "insightInstance") {
			suppressedParentOf.set(node.id, node.parentId);
		}
	});

	if (suppressedParentOf.size === 0) return nodes;

	function resolveParent(parentId: string | undefined, seen: Set<string>): string | undefined {
		if (!parentId || !suppressedParentOf.has(parentId) || seen.has(parentId)) return parentId;
		return resolveParent(suppressedParentOf.get(parentId), new Set(seen).add(parentId));
	}

	return nodes
		.filter((node) => !suppressedParentOf.has(node.id))
		.map((node) =>
			node.parentId && suppressedParentOf.has(node.parentId)
				? { ...node, parentId: resolveParent(node.parentId, new Set()) }
				: node
		);
}

/**
 * Maps the digest's node list into ReactFlow's shape, preserving the
 * server-computed ELK containment hierarchy (CTX-108) instead of the flat
 * absolute-position list CTX-97 originally shipped:
 *
 * - `position` stays relative to `parentId` (never converted to root
 *   coordinates) — ReactFlow itself walks the `parentId` chain to compute
 *   each node's absolute position, but only if parents are ordered before
 *   children in the array (`sortParentsBeforeChildren`) and every `parentId`
 *   resolves within the same response (a dangling reference throws inside
 *   ReactFlow's internals, so it's defensively dropped instead — CTX-113:
 *   the dropped node is tagged `data.orphaned` for an explicit "unassociated"
 *   treatment rather than silently reparenting it to the root).
 * - `width`/`height` survive onto both the top-level `Node` fields (ReactFlow
 *   uses these for `fitView`/extent-clamp bounds before the DOM has a chance
 *   to measure anything) and `style` (so the group node's `h-full`/`w-full`
 *   wrapper actually has a real box to fill instead of collapsing to
 *   content-size — the direct cause of the "tiny band" bug).
 * - `extent: "parent"` is only set when both dimensions are known, since
 *   ReactFlow logs a console warning ("Only child nodes can use a parent
 *   extent") if a clamped node's own size is unknown at drag time.
 *
 * Falls back to a deterministic grid position for malformed/partial payloads
 * missing `position` — never blanks valid data just because layout is absent.
 * Sibling order (route tables shelved before subnets, populated subnets
 * before empty ones, etc.) is whatever order insight-data-service already
 * produced (CTX-113) — this adapter never re-sorts siblings, only makes
 * parents precede children, which is ReactFlow's own nesting requirement.
 */
export function toReactFlowNodes(nodes: InsightTopologyResponse["nodes"]): Node<InsightNodeData>[] {
	// Canonical nested digests (`data.attributes`/`data.decorations`, camelCase
	// types) are flattened into the established `InsightNodeData` fields first —
	// a no-op (same array reference) for the previous flattened format.
	const visibleNodes = suppressInstanceContainers(normalizeCanonicalTopologyNodes(nodes));
	const nodeIds = new Set(visibleNodes.map((node) => node.id));
	let nextFallbackSlot = 0;

	const mapped = visibleNodes.map((node) => {
		const hasPosition =
			node.position && typeof node.position.x === "number" && typeof node.position.y === "number";
		let position: { x: number; y: number };
		if (hasPosition) {
			position = node.position as { x: number; y: number };
		} else {
			const slot = nextFallbackSlot++;
			position = { x: (slot % GRID_COLUMNS) * GRID_CELL, y: Math.floor(slot / GRID_COLUMNS) * GRID_CELL };
		}

		const data = (node.data ?? {}) as InsightNodeData;
		const groupKind = node.groupKind ?? data.groupKind;
		const kind = node.kind === "group" ? groupKind : node.type ?? data.kind;
		// A parentId that doesn't resolve within this same response would make
		// ReactFlow throw "Parent node X not found" — never trust it blindly.
		const hasParentRef = Boolean(node.parentId);
		const parentId = node.parentId && nodeIds.has(node.parentId) ? node.parentId : undefined;
		const orphaned = hasParentRef && !parentId;
		const hasDimensions = typeof node.width === "number" && typeof node.height === "number";

		const rfNode: Node<InsightNodeData> = {
			id: node.id,
			type: insightNodeTypeFor(kind, node.kind),
			position,
			parentId,
			extent: parentId && hasDimensions ? "parent" : undefined,
			width: hasDimensions ? node.width : undefined,
			height: hasDimensions ? node.height : undefined,
			data: {
				...data,
				label: data.label ?? node.id,
				kind: kind ?? data.kind,
				groupKind,
				ctxrUuids: node.ctxrUuids,
				orphaned,
			},
			style: {
				...(node.style as React.CSSProperties | undefined),
				...(hasDimensions ? { width: node.width, height: node.height } : null),
			},
		};
		return rfNode;
	});

	// The digest's ELK boxes are sized for a minimal label+id card; the
	// semantic renderers show more (IPs, chips, connectivity), so undersized
	// leaf cards are re-boxed and each affected subnet's interior re-gridded
	// before any disclosure pass sees them — see `cardLayout.ts`.
	return sortParentsBeforeChildren(normalizeCardDimensions(mapped));
}

/** Stable sort guaranteeing every node appears after its `parentId` (ReactFlow's own nesting requirement) — ties (siblings, roots) keep their original relative order. Cyclic/malformed chains are treated as roots rather than looping. */
function sortParentsBeforeChildren(nodes: Node<InsightNodeData>[]): Node<InsightNodeData>[] {
	const byId = new Map(nodes.map((node) => [node.id, node]));
	const depthById = new Map<string, number>();

	function depthOf(id: string, seen: Set<string>): number {
		if (depthById.has(id)) return depthById.get(id) as number;
		const parentId = byId.get(id)?.parentId;
		const depth = !parentId || !byId.has(parentId) || seen.has(id) ? 0 : 1 + depthOf(parentId, new Set(seen).add(id));
		depthById.set(id, depth);
		return depth;
	}

	return nodes
		.map((node, index) => ({ node, index, depth: depthOf(node.id, new Set()) }))
		.sort((a, b) => a.depth - b.depth || a.index - b.index)
		.map((entry) => entry.node);
}

/**
 * Maps a failed `InsightRequestState` to a distinct title/message instead of
 * letting "unauthorized"/"not-found" fall through to a blank ReactFlow
 * canvas — CTX-97 requires every request state to be visible and distinct.
 */
function topologyErrorContent(status: InsightRequestState, errorMessage: string | null): { title: string; message: string } | null {
	switch (status) {
		case "unauthorized":
			return { title: "Not authorized", message: errorMessage || "You are not authorized to view this source." };
		case "not-found":
			return { title: "Source not found", message: errorMessage || "Source not found." };
		case "service-down":
			return { title: "Insight service unavailable", message: errorMessage || "Failed to load topology" };
		case "error":
			return { title: "Failed to load topology", message: errorMessage || "Failed to load topology" };
		default:
			return null;
	}
}

/**
 * CTX-113: a topology response on an incompatible `profile` must be rejected
 * explicitly, not rendered with this version's node/decoration assumptions
 * (a subnet's `effectiveNacl`, an ENI's `securityGroups`, etc. may not mean
 * the same thing, or may not exist at all, on an older profile).
 *
 * CTX-408: the gate only speaks for a payload that actually has content. A
 * response with no nodes carries nothing to misrender, so its `profile` says
 * nothing about whether this dashboard could have rendered it — whatever left
 * it empty. Reporting an unsupported version there sends the reader after a
 * version bug that does not exist; the caller's empty state is the honest
 * message. CTX-409 covers why the API returns empty here in the first place.
 */
function unsupportedProfileContent(topology: InsightTopologyResponse | null): { title: string; message: string } | null {
	if (!topology || isSupportedInsightTopologyProfile(topology.profile)) return null;
	if (topology.nodes.length === 0) return null;
	return {
		title: "Unsupported topology version",
		message: `This topology was produced with schema "${topology.profile || "unknown"}", which this version of the dashboard can't render safely. Reprocess this source or refresh once a supported version is available.`,
	};
}

function normalize(value: string | undefined | null): string {
	return (value ?? "").toLowerCase();
}

/** Whether any of a node's own searchable fields (label, native id, and — CTX-128 — an ENI's IPs) match the query. */
function nodeOwnFieldsMatch(data: InsightNodeData, needle: string): boolean {
	return (
		normalize(data.label).includes(needle) ||
		normalize(data.nativeId).includes(needle) ||
		normalize(typeof data.privateIp === "string" ? data.privateIp : undefined).includes(needle) ||
		normalize(typeof data.publicIp === "string" ? data.publicIp : undefined).includes(needle)
	);
}

function chipRefMatches(ref: InsightChipRef | null | undefined, needle: string): boolean {
	if (!ref) return false;
	return normalize(ref.label).includes(needle) || normalize(ref.id).includes(needle) || normalize(ref.nativeId).includes(needle);
}

function connectivityMatches(connectivity: InsightConnectivityRef[] | undefined, needle: string): boolean {
	return Array.isArray(connectivity) && connectivity.some((target) => chipRefMatches(target, needle));
}

/**
 * CTX-113/CTX-128: search must locate a subnet/ENI/route-table by its
 * *decoration* content too (the effective NACL/route-table's name or id, a
 * security group's name or id, a proven connectivity target, an ENI's IP or
 * resolved instance), not just the node's own label — "search by NACL/SG
 * name or ID highlights the owning subnet/ENI" — so a match on a decoration
 * counts as a match on the node that carries it, even though the node's own
 * label doesn't contain the query.
 */
function nodeMatchesSearch(data: InsightNodeData, needle: string): boolean {
	if (nodeOwnFieldsMatch(data, needle)) return true;
	if (chipRefMatches(data.effectiveNacl, needle)) return true;
	if (chipRefMatches(data.effectiveRouteTable, needle)) return true;
	if (Array.isArray(data.securityGroups) && data.securityGroups.some((sg) => chipRefMatches(sg, needle))) return true;
	if (chipRefMatches(data.resolvedInstance, needle)) return true;
	if (connectivityMatches(data.connectivity as InsightConnectivityRef[] | undefined, needle)) return true;
	return false;
}

/**
 * ReactFlow canvas for the source-first Insight topology (CTX-97/CTX-113).
 * Keeps the useful legacy interactions (pan/zoom/drag, `Controls`,
 * `Background`) but renders from insight-data-service's already-deduplicated,
 * pre-ordered node list instead of a static `output-topology.json` fetch.
 * Clicking a node selects it as the active resource (shared via
 * `insightScopeSlice.selectedResourceId`) for `ResourceDetailPanel`/
 * `FindingsPanel` to key off of.
 *
 * CTX-113 "edge-free overview policy": the digest's `edges` list is never
 * turned into a ReactFlow edge here (`NO_EDGES`, always). Containment
 * (`parentId`), ordering (server-provided sibling order), and per-node
 * decorations (a subnet's `effectiveNacl` chip, an ENI's `securityGroups`
 * chips) communicate every association the old generic renderer used to
 * draw as a permanent line. `topology.edges` is kept in the wire contract
 * for a future focused-path/traffic mode, deliberately not built here.
 */
const InsightTopologyCanvasInner: React.FC<InsightTopologyCanvasProps> = ({
	topology,
	status,
	errorMessage,
	onRetry,
	selectedResourceId,
	onSelectResource,
	emptyDescription,
	search = "",
}) => {
	const { setViewport } = useReactFlow();

	// CTX-173: the canvas's own *rendered* pixel size (not a hardcoded
	// "typical laptop" guess) drives both how many compact overview cards
	// `hierarchyDisclosure` packs into one screen and how low the initial
	// `fitView` is allowed to go — see `adaptiveViewport.ts`. `null` until the
	// first layout pass measures a non-zero box; every render before that
	// (and every existing test that never mocks `clientWidth`/`clientHeight`)
	// falls back to the pre-adaptive fixed defaults below, so nothing here
	// changes behavior for a canvas whose real size is unknown.
	const canvasRef = useRef<HTMLDivElement | null>(null);
	const [measuredViewportSize, setMeasuredViewportSize] = useState<ViewportPixelSize | null>(null);

	useEffect(() => {
		const el = canvasRef.current;
		if (!el) return;
		const measure = () => setMeasuredViewportSize({ width: el.clientWidth, height: el.clientHeight });
		measure();
		if (typeof ResizeObserver === "undefined") return;
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const hasMeasuredViewport = Boolean(measuredViewportSize && measuredViewportSize.width > 0 && measuredViewportSize.height > 0);

	const overviewCapacity = useMemo(() => {
		if (!hasMeasuredViewport) return { columns: OVERVIEW_GRID_COLUMNS, visibleLimit: DEFAULT_VISIBLE_VPC_LIMIT };
		return computeAdaptiveOverviewCapacity(measuredViewportSize as ViewportPixelSize, {
			minVisibleCount: OVERVIEW_GRID_COLUMNS,
			maxVisibleCount: DEFAULT_VISIBLE_VPC_LIMIT * 4,
		});
	}, [hasMeasuredViewport, measuredViewportSize]);

	// The AZ-level compact grid is the same card footprint nested one level
	// deeper (inside a single focused VPC rather than the whole canvas) — reuse
	// the same adaptive capacity, only ever *tightened* by the AZ default so a
	// huge monitor doesn't suddenly show far more AZ cards than the product's
	// established default without a specific reason to.
	const azVisibleLimit = hasMeasuredViewport ? Math.min(overviewCapacity.visibleLimit, DEFAULT_VISIBLE_AZ_LIMIT * 4) : DEFAULT_VISIBLE_AZ_LIMIT;

	// CTX-173: the safety-net floor for the automatic initial/recentering
	// `fitView`, distinct from the `<ReactFlow minZoom>` prop that bounds
	// manual user zoom. Computed from the same measured viewport + minimum
	// readable card size `overviewCapacity` uses, instead of a fixed magic
	// number — with the adaptive capacity above already sizing the overview
	// grid to fit at/above this floor, `fitView`'s own unclamped fit-to-bounds
	// zoom should land at or above it on its own; this is only the backstop
	// for the residual case (e.g. a single dominant container with more real
	// content than any cap accounts for) where it doesn't.
	const readableZoomFloor = hasMeasuredViewport
		? computeReadableZoomFloor()
		: PRE_MEASUREMENT_FALLBACK_MIN_ZOOM;

	const profileError = unsupportedProfileContent(topology);
	const renderableTopology = profileError ? null : topology;

	const initialNodes = useMemo(
		() => (renderableTopology ? toReactFlowNodes(renderableTopology.nodes) : []),
		[renderableTopology]
	);

	const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);

	useEffect(() => {
		setNodes(initialNodes);
	}, [initialNodes, setNodes]);

	// `useNodesInitialized` only flips true once every node's dimensions are
	// known (either the server-provided `width`/`height` we installed above,
	// or a DOM measurement) — fitting before that would frame a 0-sized graph.
	const nodesInitialized = useNodesInitialized();

	const matchingNodeIds = useMemo(() => {
		const needle = search.trim().toLowerCase();
		if (!needle) return null;
		return new Set(nodes.filter((node) => nodeMatchesSearch(node.data ?? {}, needle)).map((node) => node.id));
	}, [nodes, search]);

	const searchQuery = search.trim().toLowerCase();

	// CTX-167 "semantic zoom": below this zoom level a card shows only its
	// boundary/name/counts/warning badges — the same info a compact VPC/AZ
	// summary card already shows — rather than per-item detail (an ENI's
	// IP/resolved-instance/security-group chips) that's illegible at that
	// scale anyway. Defaults to `1` (full detail) until the user actually
	// zooms, so every existing fixture/test that never drives a real
	// viewport keeps rendering full detail unchanged.
	const [zoom, setZoom] = useState(1);
	const lowDetail = zoom < 0.4;

	// CTX-167 hierarchy-aware disclosure: which VPC/AZ is focused, plus which
	// VPCs/AZs the user explicitly pinned open, and which bounded-count caps
	// (VPC/AZ/subnet/regional) the user bypassed via a "+N" overflow card.
	// Kept out of the initial-fitView effect's own dependencies (it depends on
	// `hierarchyResult.effectiveFocusedVpcId`/`effectiveFocusedAzId` instead) so
	// an ENI-level expand/collapse never resets pan/zoom, exactly like the
	// pre-existing `expandedSubnetIds` contract below.
	const [hierarchyFocus, setHierarchyFocus] = useState<HierarchyFocusState>(DEFAULT_HIERARCHY_FOCUS);


	// CTX-128 bounded progressive disclosure: user-toggled subnet expansion is
	// local, presentational state — it never touches `initialNodes`/`nodes`,
	// so expanding/collapsing a subnet never resets pan/zoom. A hidden ENI
	// matching the active search is force-revealed for this render via
	// `matchingNodeIds` without being added to `expandedSubnetIds` — clearing
	// the search collapses it again. Kept as an ordered stack (not just a Set)
	// so breadcrumb "Back"/Escape can deterministically collapse the
	// most-recently-expanded subnet one at a time (CTX-167).
	const [expandedSubnetOrder, setExpandedSubnetOrder] = useState<string[]>([]);
	const expandedSubnetIds = useMemo(() => new Set(expandedSubnetOrder), [expandedSubnetOrder]);
	const toggleSubnetExpanded = useCallback((subnetId: string) => {
		setExpandedSubnetOrder((prev) => (prev.includes(subnetId) ? prev.filter((id) => id !== subnetId) : [...prev, subnetId]));
	}, []);

	// Identity of the loaded source. Canonical node ids are stable hashes of
	// (type, scopeKey, nativeId), so the SAME VPC observed by two reflectors
	// keeps the same id across sources — focus/pin state from reflector A
	// would silently apply to reflector B's digest (observed live 2026-08-04:
	// switching sources with VPCs open left the camera framing the old
	// layout, a blank canvas). Switching sources resets the disclosure state,
	// and the camera effect below re-frames the new overview (`sourceKey` is
	// in its dependency list).
	const sourceKey = topology ? `${topology.sourceType}:${topology.sourceId}` : "none";
	useEffect(() => {
		setHierarchyFocus(DEFAULT_HIERARCHY_FOCUS);
		setExpandedSubnetOrder([]);
	}, [sourceKey]);

	const focusVpc = useCallback((vpcId: string) => {
		setHierarchyFocus((prev) => {
			// Opening another VPC keeps the previous one expanded (auto-pin) —
			// the user closes VPCs explicitly via each card's collapse control,
			// never implicitly by focusing a sibling.
			const pinnedVpcIds = new Set(prev.pinnedVpcIds);
			if (prev.focusedVpcId && prev.focusedVpcId !== vpcId) pinnedVpcIds.add(prev.focusedVpcId);
			pinnedVpcIds.delete(vpcId);
			return { ...prev, focusedVpcId: vpcId, focusedAzId: null, pinnedVpcIds };
		});
		setExpandedSubnetOrder([]);
	}, []);

	/** Explicit per-card collapse: clears the VPC from focus and from the pinned (kept-open) set. */
	const collapseVpc = useCallback((vpcId: string) => {
		setHierarchyFocus((prev) => {
			const pinnedVpcIds = new Set(prev.pinnedVpcIds);
			pinnedVpcIds.delete(vpcId);
			return {
				...prev,
				focusedVpcId: prev.focusedVpcId === vpcId ? null : prev.focusedVpcId,
				focusedAzId: prev.focusedVpcId === vpcId ? null : prev.focusedAzId,
				pinnedVpcIds,
			};
		});
		// Collapsing a VPC has to take everything opened *inside* it with it —
		// the AZ (above) and any expanded subnets. Leaving subnets expanded left
		// them stranded in a collapsed VPC, so reopening it looked "stuck".
		setExpandedSubnetOrder([]);
	}, []);

	/** Collapses an expanded AZ back to its compact card, dropping any subnets expanded inside it. */
	const collapseAz = useCallback((azId: string) => {
		setHierarchyFocus((prev) => {
			const pinnedAzIds = new Set(prev.pinnedAzIds);
			pinnedAzIds.delete(azId);
			return {
				...prev,
				focusedAzId: prev.focusedAzId === azId ? null : prev.focusedAzId,
				pinnedAzIds,
			};
		});
		setExpandedSubnetOrder([]);
	}, []);
	const focusAz = useCallback((azId: string, vpcId?: string) => {
		setHierarchyFocus((prev) => {
			// An AZ card inside a *pinned* (non-focused) VPC is otherwise a dead
			// control: `focusedAzId` only takes effect for the focused VPC, so
			// clicking it must also transfer VPC focus there — auto-pinning the
			// previously focused VPC, exactly like focusing the VPC directly.
			if (!vpcId || vpcId === prev.focusedVpcId) return { ...prev, focusedAzId: azId };
			const pinnedVpcIds = new Set(prev.pinnedVpcIds);
			if (prev.focusedVpcId) pinnedVpcIds.add(prev.focusedVpcId);
			pinnedVpcIds.delete(vpcId);
			return { ...prev, focusedVpcId: vpcId, focusedAzId: azId, pinnedVpcIds };
		});
		setExpandedSubnetOrder([]);
	}, []);
	const togglePinVpc = useCallback((vpcId: string) => {
		setHierarchyFocus((prev) => {
			const pinnedVpcIds = new Set(prev.pinnedVpcIds);
			if (pinnedVpcIds.has(vpcId)) pinnedVpcIds.delete(vpcId);
			else pinnedVpcIds.add(vpcId);
			return { ...prev, pinnedVpcIds };
		});
	}, []);
	const togglePinAz = useCallback((azId: string) => {
		setHierarchyFocus((prev) => {
			const pinnedAzIds = new Set(prev.pinnedAzIds);
			if (pinnedAzIds.has(azId)) pinnedAzIds.delete(azId);
			else pinnedAzIds.add(azId);
			return { ...prev, pinnedAzIds };
		});
	}, []);
	const expandScopeLevel = useCallback((level: unknown) => {
		setHierarchyFocus((prev) => {
			switch (level) {
				case "vpc":
					return { ...prev, vpcsExpanded: true };
				case "az":
					return { ...prev, azsExpanded: true };
				case "subnet":
					return { ...prev, subnetsExpanded: true };
				case "regional":
					return { ...prev, regionalExpanded: true };
				case "shelf":
					// Two-way: the shelf's toggle card stays mounted when expanded
					// (as "Show fewer"), unlike the one-way levels above.
					return { ...prev, shelfExpanded: !prev.shelfExpanded };
				default:
					return prev;
			}
		});
	}, []);

	/** One-level-back for the breadcrumb Back button and the Escape key: collapse the most-recently-expanded subnet first, then the focused AZ, then the focused VPC. */
	const goBackOneLevel = useCallback(() => {
		if (expandedSubnetOrder.length > 0) {
			setExpandedSubnetOrder((prev) => prev.slice(0, -1));
			return;
		}
		setHierarchyFocus((prev) => {
			if (prev.focusedAzId) return { ...prev, focusedAzId: null };
			if (prev.focusedVpcId) return { ...prev, focusedVpcId: null };
			return prev;
		});
	}, [expandedSubnetOrder]);

	const navigateToRoot = useCallback(() => {
		setHierarchyFocus((prev) => ({ ...prev, focusedVpcId: null, focusedAzId: null }));
		setExpandedSubnetOrder([]);
	}, []);
	const navigateToVpc = useCallback(() => {
		setHierarchyFocus((prev) => ({ ...prev, focusedAzId: null }));
		setExpandedSubnetOrder([]);
	}, []);
	const navigateToAz = useCallback(() => {
		setExpandedSubnetOrder([]);
	}, []);

	const canGoBack = Boolean(hierarchyFocus.focusedVpcId || hierarchyFocus.focusedAzId || expandedSubnetOrder.length > 0);

	// Escape returns one level, mirroring the breadcrumb Back button — only
	// attached while there's actually somewhere to go back to, so it never
	// swallows an unrelated Escape (e.g. a modal elsewhere) once the user is
	// already back at the account/region overview.
	useEffect(() => {
		if (!canGoBack) return;
		function onKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") goBackOneLevel();
		}
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [canGoBack, goBackOneLevel]);

	// CTX-167 "search-driven reveal": a hidden match auto-expands its whole
	// VPC/AZ/subnet ancestor chain for this render only — `hierarchyFocus`
	// itself is never mutated by search, so clearing the query restores
	// exactly the disclosure state the user had before typing, camera
	// position included, with no snapshot/restore bookkeeping needed.
	const forceExpansion = useMemo(() => computeForceExpansionForMatches(nodes, matchingNodeIds), [nodes, matchingNodeIds]);
	const forceVisibleSubnetIds = useMemo(() => {
		const ids = new Set(forceExpansion.subnetIds);
		matchingNodeIds?.forEach((id) => ids.add(id));
		return ids;
	}, [forceExpansion.subnetIds, matchingNodeIds]);

	const hierarchyResult = useMemo(
		() =>
			applyHierarchyDisclosure(nodes, hierarchyFocus, {
				forceExpandedVpcIds: forceExpansion.vpcIds,
				forceExpandedAzIds: forceExpansion.azIds,
				forceVisibleSubnetIds,
				forceVisibleRegionalIds: matchingNodeIds ?? undefined,
				visibleVpcLimit: overviewCapacity.visibleLimit,
				visibleAzLimit: azVisibleLimit,
				overviewColumns: overviewCapacity.columns,
			}),
		[
			nodes,
			hierarchyFocus,
			forceExpansion.vpcIds,
			forceExpansion.azIds,
			forceVisibleSubnetIds,
			matchingNodeIds,
			overviewCapacity.visibleLimit,
			overviewCapacity.columns,
			azVisibleLimit,
		]
	);

	// Deliberate camera recenter on an intentional focus change (initial
	// default focus included) — never on an ENI/subnet-count/search-only
	// change, so "expanding/collapsing a subnet never resets pan/zoom" (CTX-128)
	// now also holds for VPC/AZ focus.
	useEffect(() => {
		if (!nodesInitialized || hierarchyResult.nodes.length === 0) return;
		// Frame the scope the user just drilled into — the focused AZ when one
		// is focused, else the focused VPC, else the whole overview. Fitting
		// every mounted node here (the previous behavior) left the drilled-into
		// AZ as a sliver at the edge of a whole-account frame, forcing a manual
		// zoom after every drill — exactly the "default zoom harms the UX"
		// failure. The scope's container box bounds its children, so fitting
		// the one node frames its whole subtree.
		const el = canvasRef.current;
		const viewportSize = { width: el?.clientWidth ?? 0, height: el?.clientHeight ?? 0 };
		if (!(viewportSize.width > 0) || !(viewportSize.height > 0)) return;
		const focusScopeId = hierarchyResult.effectiveFocusedAzId ?? hierarchyResult.effectiveFocusedVpcId;
		const focusScopeMounted = focusScopeId != null && hierarchyResult.nodes.some((node) => node.id === focusScopeId);
		// CTX-173: both the root overview and a focused VPC/AZ now go through
		// the same repacked, capped compact-grid treatment (see
		// `hierarchyDisclosure.ts`'s subnet/regional survivor repack), so both
		// reliably land close to `readableZoomFloor` on their own — the
		// `minZoom` backstop covers the residual case where a single container
		// still has more real content than any cap accounts for.
		//
		// Computed from `hierarchyResult.nodes` directly (the exact array this
		// commit renders) instead of `fitView({ nodes: [{ id }] })`: the store
		// lookup raced the layout commit, and when the id resolved to stale
		// pre-layout bounds — or fell back to fit-everything — the camera
		// framed empty world coordinates (blank canvas) or zoomed out to dust
		// with multiple VPCs open (observed live on DISH staging, 2026-08-03).
		const target = computeScopedViewport(hierarchyResult.nodes, focusScopeMounted ? (focusScopeId as string) : null, viewportSize, {
			padding: focusScopeMounted ? 0.05 : OVERVIEW_FIT_PADDING,
			minZoom: readableZoomFloor,
			maxZoom: 1.5,
		});
		if (target) setViewport(target, { duration: cameraMoveDuration() });
		// Deliberately excludes `hierarchyResult.nodes` itself (the viewport target computed above) from this effect's own dependency list — only a genuine VPC/AZ focus change (or the initial viewport measurement completing) should recenter the camera, not every disclosure-count change (e.g. an unrelated subnet-count overflow toggling elsewhere).
	}, [nodesInitialized, hierarchyResult.effectiveFocusedVpcId, hierarchyResult.effectiveFocusedAzId, hasMeasuredViewport, readableZoomFloor, setViewport, sourceKey]);

	// CTX-167 "focuses the matching resource": recenter the camera on the
	// current search matches once their ancestor chain has been force-revealed
	// above — this never touches `hierarchyFocus`, so clearing the search
	// simply stops re-centering rather than snapping the camera anywhere.
	useEffect(() => {
		if (!nodesInitialized || !matchingNodeIds || matchingNodeIds.size === 0) return;
		const el = canvasRef.current;
		const viewportSize = { width: el?.clientWidth ?? 0, height: el?.clientHeight ?? 0 };
		if (!(viewportSize.width > 0) || !(viewportSize.height > 0)) return;
		// Same deterministic self-computed bounds as the focus-change effect
		// above — a search's force-revealed matches move in the very commit
		// that reveals them, exactly the store-lookup race `fitView` lost.
		const target = computeScopedViewport(hierarchyResult.nodes, Array.from(matchingNodeIds), viewportSize, {
			padding: 0.3,
			minZoom: 0.05,
			maxZoom: 1.5,
		});
		if (target) setViewport(target, { duration: cameraMoveDuration() });
		// `hierarchyResult.nodes` intentionally omitted, same contract as the focus-change effect above.
	}, [nodesInitialized, matchingNodeIds, setViewport]);

	const disclosureNodes = useMemo(
		() =>
			applyEniDisclosure(hierarchyResult.nodes, {
				expandedSubnetIds,
				forceVisibleIds: matchingNodeIds ?? undefined,
			}),
		[hierarchyResult.nodes, expandedSubnetIds, matchingNodeIds]
	);

	const styledNodes = useMemo(
		() =>
			disclosureNodes.map((node) => {
				const isSelected = node.id === selectedResourceId;
				const isMatch = matchingNodeIds ? matchingNodeIds.has(node.id) : true;
				const isEniOverflow = node.type === "insightEniOverflow";
				const isScopeOverflow = node.type === "insightScopeOverflow";
				const isVpcSummary = node.type === "insightVpcSummary";
				const isAzSummary = node.type === "insightAzSummary";
				const isVpcContainer = node.type === "insightVpc";
				const isAzContainer = node.type === "insightAz";
				return {
					...node,
					data: {
						...node.data,
						searchQuery: searchQuery || undefined,
						onSelectRelated: onSelectResource,
						lowDetail,
						...(isEniOverflow && node.parentId
							? { onToggleExpand: () => toggleSubnetExpanded(node.parentId as string) }
							: null),
						...(isVpcSummary ? { onFocus: () => focusVpc(node.id) } : null),
						...(isAzSummary ? { onFocus: () => focusAz(node.id, node.parentId) } : null),
						...(isScopeOverflow ? { onExpandScope: () => expandScopeLevel(node.data.scopeLevel) } : null),
						...(isVpcContainer
							? {
									pinned: hierarchyFocus.pinnedVpcIds.has(node.id),
									focused: node.id === hierarchyResult.effectiveFocusedVpcId,
									onTogglePin: () => togglePinVpc(node.id),
									onCollapse: () => collapseVpc(node.id),
								}
							: null),
						...(isAzContainer
							? {
									pinned: hierarchyFocus.pinnedAzIds.has(node.id),
									onTogglePin: () => togglePinAz(node.id),
									onCollapse: () => collapseAz(node.id),
								}
							: null),
					},
					style: {
						...node.style,
						opacity: isMatch ? 1 : 0.25,
						outline: isSelected ? "2px solid var(--color-semantic-border-brand-secondary, #2563eb)" : undefined,
					},
				};
			}),
		[
			disclosureNodes,
			selectedResourceId,
			matchingNodeIds,
			searchQuery,
			lowDetail,
			onSelectResource,
			toggleSubnetExpanded,
			focusVpc,
			focusAz,
			expandScopeLevel,
			hierarchyFocus.pinnedVpcIds,
			hierarchyFocus.pinnedAzIds,
			hierarchyResult.effectiveFocusedVpcId,
			togglePinVpc,
			togglePinAz,
			collapseVpc,
			collapseAz,
		]
	);

	const breadcrumbVpc: TopologyBreadcrumbSegment | null = useMemo(() => {
		if (!hierarchyResult.effectiveFocusedVpcId) return null;
		const node = nodes.find((candidate) => candidate.id === hierarchyResult.effectiveFocusedVpcId);
		return node ? { id: node.id, label: String(node.data?.label ?? node.id) } : null;
	}, [nodes, hierarchyResult.effectiveFocusedVpcId]);

	const breadcrumbAz: TopologyBreadcrumbSegment | null = useMemo(() => {
		if (!hierarchyResult.hasAzDimension || !hierarchyResult.effectiveFocusedAzId) return null;
		const node = nodes.find((candidate) => candidate.id === hierarchyResult.effectiveFocusedAzId);
		return node ? { id: node.id, label: String(node.data?.label ?? node.id) } : null;
	}, [nodes, hierarchyResult.hasAzDimension, hierarchyResult.effectiveFocusedAzId]);

	const lastExpandedSubnetId = expandedSubnetOrder[expandedSubnetOrder.length - 1] ?? null;
	const breadcrumbSubnet: TopologyBreadcrumbSegment | null = useMemo(() => {
		if (!lastExpandedSubnetId) return null;
		const node = nodes.find((candidate) => candidate.id === lastExpandedSubnetId);
		return node ? { id: node.id, label: String(node.data?.label ?? node.id) } : null;
	}, [nodes, lastExpandedSubnetId]);

	const hasVpcs = useMemo(() => nodes.some((node) => node.type === "insightVpc"), [nodes]);

	const errorContent = profileError ?? topologyErrorContent(status, errorMessage);

	// CTX-422: the payload's `state` says *why* this is empty, so the empty
	// title and message come from it rather than from one sentence that had
	// to cover every cause. An unrecognised state degrades to the default.
	const emptyCopy = describeEmptyState(topology);

	return (
		<DataStateView
			loading={status === "loading"}
			loadingContent={
				<div
					data-testid="topology-canvas-loading"
					className="flex h-full w-full items-center justify-center text-sm text-semantic-text-secondary"
				>
					Loading topology…
				</div>
			}
			error={errorContent?.message ?? null}
			errorTitle={errorContent?.title}
			onRetry={onRetry}
			isEmpty={status === "empty" || (renderableTopology != null && renderableTopology.nodes.length === 0)}
			emptyTitle={emptyCopy.title}
			emptyDescription={emptyDescription ?? emptyCopy.description}
		>
			<div className="relative flex h-full min-h-0 w-full flex-col gap-2">
				{hasVpcs && (
					<TopologyBreadcrumbs
						account={renderableTopology?.account ?? null}
						region={renderableTopology?.region ?? null}
						vpc={breadcrumbVpc}
						az={breadcrumbAz}
						subnet={breadcrumbSubnet}
						canGoBack={canGoBack}
						onBack={goBackOneLevel}
						onNavigateToRoot={navigateToRoot}
						onNavigateToVpc={navigateToVpc}
						onNavigateToAz={navigateToAz}
					/>
				)}
				<div ref={canvasRef} className="relative min-h-0 flex-1 rounded-sm border border-semantic-border-secondary">
					<ReactFlow
						nodes={styledNodes}
						edges={NO_EDGES}
						onNodesChange={onNodesChange}
						nodeTypes={insightReactFlowNodeTypes}
						onNodeClick={(_event, node) => onSelectResource(node.id)}
						onPaneClick={() => onSelectResource(null)}
						onMove={(_event, viewport) => setZoom(viewport.zoom)}
						minZoom={0.05}
						maxZoom={2}
						nodesDraggable={false}
						nodesConnectable={false}
						proOptions={{ hideAttribution: true }}
					>
						<Background />
						{/* Nodes are permanently static (nodesDraggable={false}), so the lock/interactivity toggle would be a dead control. */}
					<Controls showInteractive={false} />
					</ReactFlow>
				</div>
			</div>
		</DataStateView>
	);
};

/** `useReactFlow`/`useNodesInitialized` require a `ReactFlowProvider` ancestor — this is the only consumer of `InsightTopologyCanvasInner`, so it's provided here rather than pushed onto the view shell. */
export const InsightTopologyCanvas: React.FC<InsightTopologyCanvasProps> = (props) => (
	<ReactFlowProvider>
		<InsightTopologyCanvasInner {...props} />
	</ReactFlowProvider>
);

export default InsightTopologyCanvas;
